import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Users, ClipboardCheck, ShieldAlert, Paperclip, CalendarDays, AlertTriangle, Globe, Building2, BookOpen } from "lucide-react";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { NextSteps } from "../components/NextSteps";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { PortalLink } from "../components/PortalLink";
import { DimensionHeading, StatusTag, type StatusTone } from "../components/Drawing";
import { FRAMEWORK_LABELS } from "../context/FrameworksContext";
import type { FrameworkId } from "../context/FrameworksContext";
import { controlRoute } from "../utils/controlRoute";
import { fmtBytes } from "../utils/format";
import { SiteModal, SITE_KIND_LABELS } from "./Sites";
import type { Site } from "./Sites";
import { useControlTagData } from "../components/ControlTags";
import { AddendumScopeChips, freshnessBadge, type PolicyAddendum } from "../components/PolicyAddenda";
import { SiteStandardsPanel } from "../components/SiteStandardsPanel";
import { RepeatFindingsPanel } from "../components/RepeatFindingsPanel";

// One site of the organisation, looked at rather than switched into: the
// organisation stays one management system, so this page gathers what the
// organisation holds *at* this site — who is based here and who is named for
// which responsibility, the controls whose implementation is checked here,
// the risks raised here, the evidence gathered here, the policy addenda that
// apply here and the calendar tasks due here. It is what an auditor sampling
// the site under IAF MD 1 asks for. Every list links into its register
// pre-filtered to the site (?site=<id>).

interface Person { id: string; displayName: string; email: string; jobTitle: string | null }
interface Member extends Person { orgRole: { id: string; name: string; color: string } | null }
interface Responsibility { id: string; title: string; notes: string | null; createdAt: string; user: Person }
interface SiteControl { id: string; frameworkId: string; kind: "CLAUSE" | "CONTROL"; domain: string | null; name: string; status: string; applicable: boolean | null }
interface SiteRisk { id: string; displayId: number | null; title: string; category: string; status: string; priority: string | null; residualScore: number | null; reviewDate: string | null }
interface SiteEvidence { id: string; fileName: string; sizeBytes: number; uploadedAt: string; controls: Array<{ frameworkId: string; controlRef: string }> }
interface SiteEvent { id: string; title: string; eventDate: string; kind: string; frameworkId: string | null; assignee: { id: string; displayName: string } | null }

interface SiteDetailResponse {
  site: Site;
  dependents: Array<{ id: string; name: string; kind: string; country: string }>;
  members: Member[];
  responsibilities: Responsibility[];
  controls: SiteControl[];
  // Tagged to the site's country rather than the site itself.
  jurisdictionControls: SiteControl[];
  risks: SiteRisk[];
  evidence: SiteEvidence[];
  events: SiteEvent[];
  addenda: PolicyAddendum[];
  suggestedTitles: string[];
}

// Statement of Applicability pages that host the site filter (SoaFrameworkPanel).
const SOA_ROUTES: Record<string, string> = {
  iso27001: "/iso27001-soa",
  iso9001:  "/iso9001-soa",
  iso42001: "/iso42001-soa",
  iso45001: "/iso45001-soa",
  iso14001: "/iso14001-soa",
};

const SOA_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  COMPLIANT:   { label: "Compliant",     tone: "met" },
  GAP:         { label: "Gap",           tone: "gap" },
  IN_PROGRESS: { label: "In progress",   tone: "partial" },
  NOT_STARTED: { label: "Not assessed",  tone: "none" },
};

const RISK_STATUS_LABEL: Record<string, string> = {
  open: "Open", under_treatment: "In treatment", accepted: "Accepted", transferred: "Transferred", tentative_remediated: "Tentatively remediated", closed: "Closed",
};

const EVENT_KIND_LABEL: Record<string, string> = { review: "Review", audit: "Audit", renewal: "Renewal", meeting: "Meeting" };

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function frameworkLabel(id: string) {
  return FRAMEWORK_LABELS[id as FrameworkId] ?? id;
}

const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: theme.color.heading, display: "flex", alignItems: "center", gap: 8 };
const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text };
const pill = (color: string, bg: string): React.CSSProperties => ({ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 3, color, background: bg, whiteSpace: "nowrap" });
const subtleLink: React.CSSProperties = { fontSize: 12, color: theme.color.accentText, textDecoration: "none", marginLeft: "auto", whiteSpace: "nowrap" };

function Section({ icon, title, count, link, linkLabel, children }: {
  icon: React.ReactNode; title: string; count: number; link?: string; linkLabel?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ ...card, padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <DimensionHeading as="h3" count={count} style={{ ...sectionTitle, flex: 1, minWidth: 0, margin: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{icon}{title}</span>
        </DimensionHeading>
        {link && <PortalLink to={link} style={subtleLink}>{linkLabel ?? "Open"} →</PortalLink>}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, color: theme.color.faint, padding: "6px 0" }}>{children}</div>;
}

function ControlRow({ c }: { c: SiteControl }) {
  const st = SOA_STATUS[c.status] ?? SOA_STATUS.NOT_STARTED;
  return (
    <div style={rowStyle}>
      <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: theme.color.accentText, flexShrink: 0 }}>{c.id}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{c.name}</span>
      {c.applicable === false && <span style={pill(theme.color.muted, theme.color.panelAlt)}>Not applicable</span>}
      <StatusTag tone={st.tone}>{st.label}</StatusTag>
    </div>
  );
}

// ── Responsibilities ──────────────────────────────────────────────────────────

function ResponsibilitiesSection({ siteId, items, suggestedTitles, isAdmin, onChanged }: {
  siteId: string; items: Responsibility[]; suggestedTitles: string[]; isAdmin: boolean; onChanged: () => void;
}) {
  const [members, setMembers] = useState<Person[]>([]);
  const [title, setTitle]     = useState("");
  const [userId, setUserId]   = useState("");
  const [notes, setNotes]     = useState("");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  // Editing one holder reuses the form below: it fills with that row and saves in place.
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get<{ members: Person[] }>("/organizations/members").then((d) => setMembers(d.members)).catch(() => {});
  }, [isAdmin]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!title.trim() || !userId) { setError("Choose a responsibility and a person."); return; }
    setSaving(true);
    try {
      const body = { userId, title: title.trim(), notes: notes.trim() || null };
      if (editingId) await api.patch(`/sites/${siteId}/responsibilities/${editingId}`, body);
      else await api.post(`/sites/${siteId}/responsibilities`, body);
      setTitle(""); setUserId(""); setNotes(""); setEditingId(null);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, editingId ? "Could not save the responsibility" : "Could not add the responsibility"));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(r: Responsibility) {
    setEditingId(r.id); setTitle(r.title); setUserId(r.user.id); setNotes(r.notes ?? ""); setError("");
  }
  function cancelEdit() {
    setEditingId(null); setTitle(""); setUserId(""); setNotes(""); setError("");
  }

  async function remove(id: string) {
    setRemoving(id);
    try { await api.del(`/sites/${siteId}/responsibilities/${id}`); onChanged(); }
    catch { /* the list reloads on the next change */ }
    finally { setRemoving(null); }
  }

  // Group holders under their title so "Fire warden — A, B" reads as one line.
  const byTitle = useMemo(() => {
    const m = new Map<string, Responsibility[]>();
    for (const r of items) (m.get(r.title) ?? m.set(r.title, []).get(r.title)!).push(r);
    return Array.from(m.entries());
  }, [items]);

  return (
    <Section icon={<ClipboardCheck size={14} />} title="Responsibilities at this site" count={items.length}>
      {byTitle.length === 0 && <Empty>No one is named for a responsibility here yet{isAdmin ? " — add the site lead first." : "."}</Empty>}
      {byTitle.map(([t, holders]) => (
        <div key={t} style={{ ...rowStyle, alignItems: "flex-start" }}>
          <div style={{ width: 220, flexShrink: 0, fontWeight: 600, color: theme.color.heading }}>{t}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            {holders.map((h) => (
              <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span>{h.user.displayName}</span>
                <span style={{ fontSize: 11, color: theme.color.subtle }}>{h.user.jobTitle ? `${h.user.jobTitle} · ` : ""}{h.user.email}</span>
                {h.notes && <span style={{ fontSize: 11, color: theme.color.faint }}>— {h.notes}</span>}
                {isAdmin && (
                  <button onClick={() => startEdit(h)} disabled={editingId === h.id}
                    style={{ fontSize: 11, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                    {editingId === h.id ? "Editing…" : "Edit"}
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => remove(h.id)} disabled={removing === h.id}
                    style={{ fontSize: 11, color: theme.color.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                    {removing === h.id ? "Removing…" : "Remove"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {isAdmin && (
        <form onSubmit={add} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 200px" }}>
            <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Responsibility</label>
            <input list="site-responsibility-titles" style={inputStyle} value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="Site lead, Fire warden…" />
            <datalist id="site-responsibility-titles">
              {suggestedTitles.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 200px" }}>
            <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Person</label>
            <select style={inputStyle} value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Choose a member…</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.displayName}{m.jobTitle ? ` — ${m.jobTitle}` : ""}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 160px" }}>
            <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>Note (optional)</label>
            <input style={inputStyle} value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} placeholder="Deputy, out of hours…" />
          </div>
          <button type="submit" disabled={saving || !title.trim() || !userId} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            {editingId ? (saving ? "Saving…" : "Save") : (saving ? "Adding…" : "Name")}
          </button>
          {editingId && <button type="button" onClick={cancelEdit} disabled={saving} style={{ ...inputStyle, width: "auto", cursor: "pointer" }}>Cancel</button>}
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText, width: "100%" }}>{error}</div>}
          <div style={{ fontSize: 11, color: theme.color.faint, width: "100%" }}>
            Informational only — a responsibility never changes what the person can see or do. Access is set on the Team page.
          </div>
        </form>
      )}
    </Section>
  );
}

// ── People based here ─────────────────────────────────────────────────────────
// The site-side way to place members; the Team page does the same per member.

function PeopleSection({ siteId, members, isAdmin, onChanged }: { siteId: string; members: Member[]; isAdmin: boolean; onChanged: () => void }) {
  const [all, setAll]           = useState<Person[]>([]);
  const [picking, setPicking]   = useState(false);
  const [chosen, setChosen]     = useState<string[]>([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get<{ members: Person[] }>("/organizations/members").then((d) => setAll(d.members)).catch(() => {});
  }, [isAdmin]);

  const here = new Set(members.map((m) => m.id));
  const candidates = all.filter((m) => !here.has(m.id));

  async function save(userIds: string[]) {
    setSaving(true); setError("");
    try {
      await api.put(`/sites/${siteId}/members`, { userIds });
      setPicking(false); setChosen([]);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update who is based here"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section icon={<Users size={14} />} title="People based here" count={members.length} link={withSiteParam("/team", siteId)} linkLabel="Team page">
      {members.length === 0 && <Empty>No member is placed at this site yet.</Empty>}
      {members.map((m) => (
        <div key={m.id} style={rowStyle}>
          <span style={{ fontWeight: 600, color: theme.color.heading }}>{m.displayName}</span>
          {m.orgRole && <span style={pill(m.orgRole.color, m.orgRole.color + "22")}>{m.orgRole.name}</span>}
          <span style={{ fontSize: 11, color: theme.color.subtle, flex: 1 }}>{m.jobTitle ? `${m.jobTitle} · ` : ""}{m.email}</span>
          {isAdmin && (
            <button onClick={() => save(members.filter((x) => x.id !== m.id).map((x) => x.id))} disabled={saving}
              style={{ fontSize: 11, color: theme.color.danger, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Remove
            </button>
          )}
        </div>
      ))}
      {isAdmin && !picking && candidates.length > 0 && (
        <button onClick={() => setPicking(true)} style={{ marginTop: 10, padding: "5px 12px", borderRadius: 3, fontSize: 12, cursor: "pointer", border: `1px dashed ${theme.color.borderStrong}`, background: "transparent", color: theme.color.accentText }}>
          + Add people
        </button>
      )}
      {isAdmin && picking && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {candidates.map((m) => {
              const on = chosen.includes(m.id);
              return (
                <button key={m.id} type="button" onClick={() => setChosen(on ? chosen.filter((id) => id !== m.id) : [...chosen, m.id])}
                  style={{ padding: "4px 10px", borderRadius: 3, fontSize: 12, cursor: "pointer", border: `1px solid ${on ? theme.color.accent : theme.color.border}`, background: on ? theme.color.accentBg : theme.color.panel, color: on ? theme.color.accentText : theme.color.text }}>
                  {m.displayName}{m.jobTitle ? <span style={{ color: theme.color.faint }}> · {m.jobTitle}</span> : null}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => save([...members.map((m) => m.id), ...chosen])} disabled={saving || chosen.length === 0} style={{ ...btnPrimary, opacity: saving || chosen.length === 0 ? 0.6 : 1 }}>
              {saving ? "Saving…" : `Place ${chosen.length || ""} here`}
            </button>
            <button onClick={() => { setPicking(false); setChosen([]); }} style={{ fontSize: 12, color: theme.color.muted, background: "none", border: "none", cursor: "pointer" }}>Cancel</button>
            {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
          </div>
          <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>Where someone is based. It never changes what they can see or do.</div>
        </div>
      )}
    </Section>
  );
}

// ── Departments here ──────────────────────────────────────────────────────────

function DepartmentsSection({ siteId, departments, isAdmin, onChanged }: {
  siteId: string; departments: Site["departments"]; isAdmin: boolean; onChanged: () => void;
}) {
  const { departments: all } = useControlTagData();
  const [saving, setSaving] = useState(false);
  const here = departments.map((d) => d.id);

  async function toggle(id: string) {
    setSaving(true);
    try {
      await api.put(`/sites/${siteId}/departments`, { departmentIds: here.includes(id) ? here.filter((x) => x !== id) : [...here, id] });
      onChanged();
    } catch { /* the page reloads on the next change */ }
    finally { setSaving(false); }
  }

  return (
    <Section icon={<Building2 size={14} />} title="Departments at this site" count={departments.length} link="/departments" linkLabel="Departments">
      {all.length === 0 && <Empty>No departments are defined yet.</Empty>}
      {all.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 0" }}>
          {(isAdmin ? all : all.filter((d) => here.includes(d.id))).map((d) => {
            const on = here.includes(d.id);
            return (
              <button key={d.id} type="button" disabled={!isAdmin || saving} onClick={() => toggle(d.id)}
                title={isAdmin ? (on ? "Click to remove from this site" : "Click to add to this site") : undefined}
                style={{ padding: "4px 10px", borderRadius: 3, fontSize: 12, cursor: isAdmin ? "pointer" : "default", border: `1px solid ${on ? d.color : theme.color.border}`, background: on ? d.color + "22" : "transparent", color: on ? d.color : theme.color.faint, fontWeight: on ? 600 : 400 }}>
                {d.name}
              </button>
            );
          })}
          {!isAdmin && here.length === 0 && <Empty>No department is marked as operating here.</Empty>}
        </div>
      )}
      {isAdmin && all.length > 0 && <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>Which departments operate from this office. Informational, for the site list and the auditor.</div>}
    </Section>
  );
}

function withSiteParam(path: string, siteId: string) {
  return `${path}?site=${encodeURIComponent(siteId)}`;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function SiteDetailPage() {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const { user } = useCurrentUser();
  const isAdmin = !!user?.permissions?.includes("org:admin");

  const [data, setData]       = useState<SiteDetailResponse | null>(null);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [hasCentralFunction, setHasCentralFunction] = useState(false);
  const [allSites, setAllSites] = useState<Array<Pick<Site, "id" | "name" | "parentSiteId">>>([]);

  function load() {
    setLoading(true);
    api.get<SiteDetailResponse>(`/sites/${siteId}`)
      .then((d) => { setData(d); setError(""); })
      .catch((err) => setError(apiErrorMessage(err, "Site not found")))
      .finally(() => setLoading(false));
    // The other sites, for the edit modal: whether another site already directs
    // the management system, and what this one may report to.
    api.get<{ sites: Site[]; summary: { centralFunctionSiteId: string | null } }>("/sites")
      .then((d) => {
        setHasCentralFunction(!!d.summary.centralFunctionSiteId && d.summary.centralFunctionSiteId !== siteId);
        setAllSites(d.sites.map((x) => ({ id: x.id, name: x.name, parentSiteId: x.parentSiteId })));
      })
      .catch(() => {});
  }
  useEffect(() => { load(); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = useMemo(() => new Date(new Date().toDateString()).getTime(), []);
  const upcoming = useMemo(() => (data?.events ?? []).filter((e) => new Date(e.eventDate).getTime() >= today), [data, today]);
  const overdue  = useMemo(() => (data?.events ?? []).filter((e) => new Date(e.eventDate).getTime() < today), [data, today]);

  const groupByFramework = (controls: SiteControl[]) => {
    const m = new Map<string, SiteControl[]>();
    for (const c of controls) (m.get(c.frameworkId) ?? m.set(c.frameworkId, []).get(c.frameworkId)!).push(c);
    return Array.from(m.entries());
  };
  const controlsByFramework = useMemo(() => groupByFramework(data?.controls ?? []), [data]);
  const jurisdictionByFramework = useMemo(() => groupByFramework(data?.jurisdictionControls ?? []), [data]);

  if (loading && !data) return <div className="page-pad" style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;
  if (error || !data) {
    return (
      <div className="page-pad" style={{ fontFamily: theme.font }}>
        <PortalLink to="/sites" style={{ fontSize: 12, color: theme.color.accentText, textDecoration: "none" }}>← Sites &amp; locations</PortalLink>
        <div style={{ ...card, marginTop: 16, color: theme.color.dangerText, fontSize: 13 }}>{error || "Site not found"}</div>
      </div>
    );
  }

  const { site } = data;
  const address = [site.addressLine1, site.addressLine2, site.city, site.region, site.postcode].filter(Boolean).join(", ");
  const withSite = (path: string) => `${path}?site=${encodeURIComponent(site.id)}`;

  const stats: Array<{ label: string; value: React.ReactNode }> = [
    { label: "People",         value: data.members.length },
    { label: "Departments",    value: site.departments.length },
    { label: "Named roles",    value: data.responsibilities.length },
    { label: "Site controls",  value: data.controls.length },
    { label: "Risks",          value: data.risks.length },
    { label: "Evidence",       value: data.evidence.length },
    { label: "Policy addenda", value: data.addenda.length },
    { label: "Upcoming tasks", value: upcoming.length },
  ];

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <PortalLink to="/sites" style={{ fontSize: 12, color: theme.color.accentText, textDecoration: "none" }}>← Sites &amp; locations</PortalLink>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, margin: "10px 0 16px" }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <MapPin size={18} color={theme.color.accentText} />{site.name}
            {site.isCentralFunction && <span style={pill(theme.color.accentText, theme.color.accentBg)}>Central function</span>}
            <span style={site.inScope ? pill(theme.color.successText, theme.color.successBg) : pill(theme.color.muted, theme.color.panelAlt)}>{site.inScope ? "In scope" : "Out of scope"}</span>
            {!site.isLegalEstablishment && <span style={pill(theme.color.muted, theme.color.panelAlt)}>Not an establishment</span>}
          </h1>
          <div style={{ fontSize: 13, color: theme.color.subtle }}>
            {SITE_KIND_LABELS[site.kind]} · {site.country}{site.headcount != null ? ` · ${site.headcount} people` : ""}{site.legalEntityName ? ` · ${site.legalEntityName}` : ""}
          </div>
          {address && <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 2 }}>{address}</div>}
          {(site.parent || data.dependents.length > 0) && (
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {site.parent && <span>Reports to <PortalLink to={`/sites/${site.parent.id}`} style={{ color: theme.color.accentText, textDecoration: "none" }}>{site.parent.name}</PortalLink></span>}
              {data.dependents.length > 0 && (
                <span>
                  {data.dependents.length === 1 ? "Reporting here:" : `${data.dependents.length} sites report here:`}{" "}
                  {data.dependents.map((d, i) => (
                    <React.Fragment key={d.id}>{i > 0 && ", "}<PortalLink to={`/sites/${d.id}`} style={{ color: theme.color.accentText, textDecoration: "none" }}>{d.name}</PortalLink></React.Fragment>
                  ))}
                </span>
              )}
            </div>
          )}
          {site.notes && <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 6, maxWidth: 640, whiteSpace: "pre-wrap" }}>{site.notes}</div>}
        </div>
        {isAdmin && <button onClick={() => setEditing(true)} style={{ ...btnPrimary, flexShrink: 0 }}>Edit site</button>}
      </div>

      {/* What the scope statement and a multi-site audit (IAF MD 1) need to know about a site. */}
      <NextSteps
        items={[
          { done: !!(site.addressLine1 && site.city && site.postcode), label: "Record the full address" },
          { done: site.headcount != null, label: "Record how many people work here", hint: "Sets the audit sample for a multi-site certificate." },
          ...(site.isLegalEstablishment ? [{ done: !!site.legalEntityName?.trim(), label: "Name the legal entity that operates it" }] : []),
          { done: data.responsibilities.length > 0, label: "Name who is responsible at this site", hint: "A site lead, a first aider, a fire warden: whatever the site needs." },
          { done: data.members.length > 0, label: "Say who works here" },
          ...(!site.inScope ? [{ done: !!site.notes?.trim(), label: "Record why the site is out of scope", hint: "An auditor asks for the justification for every exclusion." }] : []),
        ]}
        action={isAdmin ? <button type="button" onClick={() => setEditing(true)} style={btnSecondary}>Fill these in</button> : undefined}
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        {stats.map((s) => (
          <div key={s.label} style={{ ...card, padding: "10px 14px", minWidth: 120, flex: "1 1 120px" }}>
            <div style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading, marginTop: 2 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SiteStandardsPanel siteId={site.id} canSetStandards={isAdmin} canSetControls={!!user?.permissions?.includes("controls:write")} />

        <RepeatFindingsPanel scope="sites" siteId={site.id} />

        <ResponsibilitiesSection siteId={site.id} items={data.responsibilities} suggestedTitles={data.suggestedTitles} isAdmin={isAdmin} onChanged={load} />

        <PeopleSection siteId={site.id} members={data.members} isAdmin={isAdmin} onChanged={load} />

        <DepartmentsSection siteId={site.id} departments={site.departments} isAdmin={isAdmin} onChanged={load} />

        <Section icon={<ClipboardCheck size={14} />} title="Site-specific controls" count={data.controls.length}>
          {data.controls.length === 0 && (
            <Empty>No control is tagged to this office. On any standard's page, use "+ Tag" on a control whose implementation is checked here — physical entry, OH&amp;S hazards, environmental aspects. Every other control applies here through the organisation-wide management system.</Empty>
          )}
          {controlsByFramework.map(([fw, controls]) => (
            <div key={fw} style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 0 2px" }}>
                {frameworkLabel(fw)}
                <PortalLink to={withSite(SOA_ROUTES[fw] ?? controlRoute(controls[0].id, fw))} style={subtleLink}>Open in SOA →</PortalLink>
              </div>
              {controls.map((c) => <ControlRow key={c.id} c={c} />)}
            </div>
          ))}
        </Section>

        {jurisdictionByFramework.length > 0 && (
          <Section icon={<Globe size={14} />} title={`Applies here through the ${site.country} jurisdiction`} count={data.jurisdictionControls.length}>
            <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 4 }}>Controls tagged to {site.country} rather than to this office; every site in the country carries them.</div>
            {jurisdictionByFramework.map(([fw, controls]) => (
              <div key={fw} style={{ marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 0 2px" }}>
                  {frameworkLabel(fw)}
                  <PortalLink to={`${SOA_ROUTES[fw] ?? controlRoute(controls[0].id, fw)}?jurisdiction=${encodeURIComponent(site.country)}`} style={subtleLink}>Open in SOA →</PortalLink>
                </div>
                {controls.map((c) => <ControlRow key={c.id} c={c} />)}
              </div>
            ))}
          </Section>
        )}

        <Section icon={<ShieldAlert size={14} />} title="Risks raised here" count={data.risks.length} link={withSite("/risk-register")} linkLabel="Risk register">
          {data.risks.length === 0 && <Empty>No risk names this site. Set the site on a risk in the register to see it here.</Empty>}
          {data.risks.map((r) => (
            <div key={r.id} style={rowStyle}>
              <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: theme.color.subtle, flexShrink: 0 }}>{r.displayId ? `RISK-${String(r.displayId).padStart(3, "0")}` : "—"}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{r.title}<span style={{ fontSize: 11, color: theme.color.subtle }}> · {r.category}</span></span>
              {r.priority && <span style={pill(theme.color.warningText, theme.color.warningBg)}>{r.priority}</span>}
              {r.residualScore != null && <span title="Residual score" style={{ fontSize: 11, color: theme.color.muted }}>{r.residualScore}</span>}
              <span style={pill(theme.color.accentText, theme.color.accentBg)}>{RISK_STATUS_LABEL[r.status] ?? r.status}</span>
            </div>
          ))}
        </Section>

        <Section icon={<Paperclip size={14} />} title="Evidence gathered here" count={data.evidence.length} link={withSite("/documents")} linkLabel="Evidence repository">
          {data.evidence.length === 0 && <Empty>No evidence is tagged to this site. Choose the site on a document in the evidence repository.</Empty>}
          {data.evidence.map((d) => (
            <div key={d.id} style={rowStyle}>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.fileName}</span>
              {d.controls.slice(0, 3).map((c) => (
                <span key={`${c.frameworkId}:${c.controlRef}`} style={{ fontSize: 10, fontVariantNumeric: "tabular-nums", color: theme.color.accentText, background: theme.color.accentBg, borderRadius: 3, padding: "0 4px" }}>{c.controlRef}</span>
              ))}
              {d.controls.length > 3 && <span style={{ fontSize: 10, color: theme.color.subtle }}>+{d.controls.length - 3}</span>}
              <span style={{ fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap" }}>{fmtBytes(d.sizeBytes)} · {fmtDate(d.uploadedAt)}</span>
            </div>
          ))}
        </Section>

        <Section icon={<BookOpen size={14} />} title="Policy addenda applying here" count={data.addenda.length} link="/templates" linkLabel="Policy templates">
          {data.addenda.length === 0 && <Empty>No policy addendum names this site. On Policy Templates, add an addendum to a policy and tag it with this site — for local law, a works council agreement or the landlord's rules.</Empty>}
          {data.addenda.map((a) => {
            const badge = freshnessBadge(a.freshness);
            return (
              <div key={a.id} style={{ ...rowStyle, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <PortalLink to={`/templates?template=${encodeURIComponent(a.templateId)}`} style={{ color: theme.color.heading, fontWeight: 600, textDecoration: "none" }}>
                    {a.templateTitle} — {a.title}
                  </PortalLink>
                  <div style={{ marginTop: 4 }}><AddendumScopeChips addendum={a} /></div>
                </div>
                {a.latestVersion && <span style={{ fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap" }}>v{a.latestVersion.versionNumber} · {fmtDate(a.latestVersion.createdAt)}</span>}
                {badge && <span style={pill(badge.color, badge.bg)}>{badge.label}</span>}
              </div>
            );
          })}
        </Section>

        <Section icon={<CalendarDays size={14} />} title="Calendar tasks at this site" count={data.events.length} link={withSite("/schedule")} linkLabel="Review calendar">
          {data.events.length === 0 && <Empty>No task names this site. Add one on the Review Calendar and choose the site and the person responsible; they are reminded a week before and on the day.</Empty>}
          {[...overdue, ...upcoming].map((e) => {
            const late = new Date(e.eventDate).getTime() < today;
            return (
              <div key={e.id} style={rowStyle}>
                <span style={{ fontSize: 12, color: late ? theme.color.dangerText : theme.color.subtle, whiteSpace: "nowrap", width: 96, flexShrink: 0, display: "flex", alignItems: "center", gap: 4 }}>
                  {late && <AlertTriangle size={11} />}{fmtDate(e.eventDate)}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>{e.title}</span>
                <span style={pill(theme.color.muted, theme.color.panelAlt)}>{EVENT_KIND_LABEL[e.kind] ?? e.kind}</span>
                <span style={{ fontSize: 11, color: e.assignee ? theme.color.text : theme.color.faint, whiteSpace: "nowrap" }}>{e.assignee ? e.assignee.displayName : "Unassigned"}</span>
              </div>
            );
          })}
        </Section>
      </div>

      <p style={{ fontSize: 12, color: theme.color.subtle, marginTop: 16, maxWidth: 640, lineHeight: 1.5 }}>
        A multi-site certificate is audited by sampling sites (IAF MD 1). This page is the site's side of that visit; the
        organisation's SOA, policies and score are shared by every site and stay on their own pages — an addendum is how a
        policy says more for this site.
      </p>

      {editing && (
        <SiteModal initial={site} hasCentralFunction={hasCentralFunction} sites={allSites} onClose={() => setEditing(false)} onSaved={() => load()} />
      )}
    </div>
  );
}
